/**
 * AffiliateExitEventsPanel — Super Admin viewer for the affiliate exit policy
 * audit trail.
 *
 * Backed by /api/admin/affiliate-exit-events. Rows come from the
 * `affiliate_exit_events` collection that the exit policy module
 * (affiliate_exit_policy.py) writes to whenever an affiliate is banned,
 * deletes their account, has their subscription cancelled, or trips the
 * MIA cron after extended inactivity.
 *
 * Lives next to the Reports tab in SuperAdmin since both are about
 * "things that need an admin eyeball."
 */
import { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { Sword, Funnel, ArrowsClockwise, ListBullets, UserMinus, Clock, Sparkle } from "@phosphor-icons/react";
import { api } from "@/lib/api";

const TYPE_BADGES = {
  banned: { bg: "bg-[#FCEDE6]", fg: "text-[#A85E4A]", label: "Banned", Icon: Sword },
  account_deleted: { bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]", label: "Account deleted", Icon: UserMinus },
  subscription_cancelled: { bg: "bg-[#F8F1E9]", fg: "text-[#8A5A00]", label: "Sub cancelled", Icon: Clock },
  mia_flagged: { bg: "bg-[#F8F1E9]", fg: "text-[#8A5A00]", label: "MIA flagged", Icon: Clock },
  reinstated: { bg: "bg-[#E8F0E9]", fg: "text-[#2F6B3A]", label: "Reinstated", Icon: Sparkle },
};

const ALL_TYPES = ["banned", "account_deleted", "subscription_cancelled", "mia_flagged", "reinstated"];

const fmtDate = (iso) => {
  if (!iso) return "—";
  try { return new Date(iso).toLocaleString(undefined, { month: "short", day: "numeric", year: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return iso; }
};

export default function AffiliateExitEventsPanel() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ limit: "200" });
      if (filterType) params.set("event_type", filterType);
      const r = await api.get(`/admin/affiliate-exit-events?${params.toString()}`);
      setEvents(r.data?.events || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Couldn't load exit events.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filterType]);

  const counts = useMemo(() => {
    const out = { _all: events.length };
    for (const e of events) out[e.event_type] = (out[e.event_type] || 0) + 1;
    return out;
  }, [events]);

  return (
    <section className="mt-12 mb-16" data-testid="admin-exit-events-panel">
      <div className="flex items-end justify-between gap-3 flex-wrap mb-3">
        <div>
          <h2 className="font-serif-display text-2xl flex items-center gap-2">
            <ListBullets size={20} weight="duotone" className="text-[#4A5D4E]" /> Creator Pro exit events
          </h2>
          <p className="text-xs text-[#6B6A68] mt-1 max-w-2xl">
            Audit trail of every Creator Pro status transition. Rows are written automatically by the exit policy module
            (bans, account deletions, sub cancellations, MIA cron flags). Surface here so admins can spot patterns and
            cross-reference reports.
          </p>
        </div>
        <button
          type="button"
          onClick={load}
          disabled={loading}
          data-testid="admin-exit-events-refresh"
          className="text-xs text-[#6B6A68] hover:text-[#1C1B1A] flex items-center gap-1.5 disabled:opacity-50"
        >
          <ArrowsClockwise size={12} weight="bold" className={loading ? "animate-spin" : ""} /> Refresh
        </button>
      </div>

      {/* Type filter pills */}
      <div className="flex items-center gap-2 mb-3 flex-wrap">
        <Funnel size={12} className="text-[#6B6A68]" />
        <button
          type="button"
          onClick={() => setFilterType(null)}
          data-testid="admin-exit-events-filter-all"
          className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border hairline ${
            filterType === null
              ? "bg-[#1C1B1A] text-[#FDFCF8] border-[#1C1B1A]"
              : "bg-white text-[#6B6A68] hover:border-[#4A5D4E]"
          }`}
        >
          All ({counts._all})
        </button>
        {ALL_TYPES.map((t) => {
          const meta = TYPE_BADGES[t];
          return (
            <button
              key={t}
              type="button"
              onClick={() => setFilterType(t === filterType ? null : t)}
              data-testid={`admin-exit-events-filter-${t}`}
              className={`text-[10px] uppercase tracking-[0.18em] px-2 py-1 rounded border hairline ${
                filterType === t
                  ? `${meta.bg} ${meta.fg} border-current`
                  : "bg-white text-[#6B6A68] hover:border-[#4A5D4E]"
              }`}
            >
              {meta.label}{counts[t] ? ` (${counts[t]})` : ""}
            </button>
          );
        })}
      </div>

      <div className="bg-white border hairline rounded-lg overflow-x-auto" data-testid="admin-exit-events-table">
        <table className="w-full text-sm min-w-[800px]">
          <thead className="bg-[#F0F1EE] text-[#6B6A68] text-xs uppercase tracking-wide">
            <tr>
              <th className="text-left px-4 py-3 w-44">When</th>
              <th className="text-left px-4 py-3">Creator Pro</th>
              <th className="text-left px-4 py-3 w-40">Event</th>
              <th className="text-left px-4 py-3">Reason / context</th>
              <th className="text-right px-4 py-3 w-32">Tier at exit</th>
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6B6A68] text-sm">Loading…</td></tr>}
            {!loading && events.length === 0 && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-[#6B6A68] text-sm">
                {filterType ? `No ${TYPE_BADGES[filterType]?.label || filterType} events yet.` : "No exit events yet — clean slate."}
              </td></tr>
            )}
            {!loading && events.map((e) => {
              const meta = TYPE_BADGES[e.event_type] || { bg: "bg-[#F0F1EE]", fg: "text-[#6B6A68]", label: e.event_type, Icon: ListBullets };
              const Icon = meta.Icon;
              return (
                <tr key={e.id || `${e.affiliate_user_id}-${e.created_at}`} className="border-t hairline" data-testid={`admin-exit-event-row-${e.id || e.affiliate_user_id}`}>
                  <td className="px-4 py-3 text-[#6B6A68] font-mono text-xs">{fmtDate(e.created_at)}</td>
                  <td className="px-4 py-3">
                    <div className="text-[#1C1B1A]">{e.affiliate_email}</div>
                    {e.affiliate_name && <div className="text-[#6B6A68] text-xs">{e.affiliate_name}</div>}
                  </td>
                  <td className="px-4 py-3">
                    <span className={`inline-flex items-center gap-1 text-[10px] uppercase tracking-[0.16em] px-1.5 py-0.5 rounded ${meta.bg} ${meta.fg} font-medium`}>
                      <Icon size={11} weight="duotone" /> {meta.label}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-[#6B6A68] text-sm">
                    {e.reason || <span className="italic text-[#A8A6A0]">—</span>}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <span className="text-[10px] uppercase tracking-[0.16em] text-[#4A5D4E]">
                      {e.tier_at_exit || (e.affiliate_is_active ? "Active" : "Inactive")}
                    </span>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </section>
  );
}
